import React, { useMemo, ReactNode } from 'react';

import { GridProviderResponse } from '../types/gridResponses';

import {
  FacetsContext,
  GridMetaContext,
  ProductsContext,
} from './GridDataContexts';

interface SlicedGridProviderProps {
  data: GridProviderResponse;
  children: ReactNode;
}

/**
 * Shared provider that splits a full grid response into three concern-specific
 * context slices. Each slice is memoized independently so that consumers only
 * re-render when the fields they subscribe to actually change.
 *
 * Used by CIOBrowseProvider, CIOSearchProvider, and ProductsV2Provider.
 */
const SlicedGridProvider = ({ data, children }: SlicedGridProviderProps) => {
  // Destructure so each field is its own variable — satisfies react-hooks/exhaustive-deps
  // and keeps each useMemo scoped to exactly the fields that matter for that slice.
  const {
    products,
    fetchNextPage,
    hasNextPage,
    isPrimaryProductsLoading,
    isPrimaryProductsFetching,
    isRelatedProductsLoading,
    isRelatedProductsFetching,
    isProductPricingLoading,
    isProductPricingFetching,
    facets,
    sortOptions,
    selectedUrlFilters,
    allRegisteredFacets,
    allRegisteredFacetNames,
    totalNumResults,
    type,
    customFacetFilters,
    setCustomFacetFilters,
    cioFilterName,
    cioFilterValue,
    redirectUrl,
    searchQuery,
  } = data;

  const productsValue = useMemo(
    () => ({
      products,
      fetchNextPage,
      hasNextPage,
      isPrimaryProductsLoading,
      isPrimaryProductsFetching,
      isRelatedProductsLoading,
      isRelatedProductsFetching,
      isProductPricingLoading,
      isProductPricingFetching,
    }),
    [
      products,
      fetchNextPage,
      hasNextPage,
      isPrimaryProductsLoading,
      isPrimaryProductsFetching,
      isRelatedProductsLoading,
      isRelatedProductsFetching,
      isProductPricingLoading,
      isProductPricingFetching,
    ]
  );

  const facetsValue = useMemo(
    () => ({
      facets,
      sortOptions,
      selectedUrlFilters,
      allRegisteredFacets,
      allRegisteredFacetNames,
      totalNumResults,
    }),
    [
      facets,
      sortOptions,
      selectedUrlFilters,
      allRegisteredFacets,
      allRegisteredFacetNames,
      totalNumResults,
    ]
  );

  const metaValue = useMemo(
    () => ({
      type,
      customFacetFilters,
      setCustomFacetFilters,
      cioFilterName,
      cioFilterValue,
      redirectUrl,
      searchQuery,
    }),
    [
      type,
      customFacetFilters,
      setCustomFacetFilters,
      cioFilterName,
      cioFilterValue,
      redirectUrl,
      searchQuery,
    ]
  );

  return (
    <ProductsContext.Provider value={productsValue}>
      <FacetsContext.Provider value={facetsValue}>
        <GridMetaContext.Provider value={metaValue}>
          {children}
        </GridMetaContext.Provider>
      </FacetsContext.Provider>
    </ProductsContext.Provider>
  );
};

export default SlicedGridProvider;
